//@flow
// The screen is created for a specific client usage and assumptions
// To be revamped into proper 'Territory' entity
import * as React from 'react';
import * as Immutable from 'immutable';
import { compose } from 'recompose';
import { translate, type TFunction } from 'react-i18next';
import type { RouterHistory } from 'react-router-dom';

import {
    getCustomDistributorField,
    isDistributorOutletsLoading,
    isNonDistributorOutletsLoading,
    isHomebaseLoading,
    isSortInProgress,
    isUpdateOutletInProgress,
    isUnassignOutletInProgress,
    isCreateMapInProgress
} from 'common/redux/selectors/territoryPlanningSelectors';
import {
    clientIdSelector,
    personnelSelector
} from 'common/redux/selectors/sessionSelector';
import { ClientSelectors } from 'common/redux/selectors/clientSelector';
import TerritoryPlanningActions from 'common/redux/actions/territoryPlanning';
import {
    FilterBuilder,
    FilterOperation,
    LogicalOperation
} from 'common/models/internal/Filter';
import type { MasterPersonnel } from 'common/models/internal/MasterPersonnel';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import { Map as LeafletMap, ZoomControl, LeafletConsumer } from 'react-leaflet';
import {
    GoogleLayer,
    MapTypeEnum
} from 'common/components/core/leaflet/GoogleLayer';
import InnerPolygonMarker from 'common/utils/InnerPolygonMarker';
import { neoUIColors } from 'common/enums/ColorEnum';
import OutletMarker from './OutletMarker';
import OutletActions from 'common/redux/actions/outlet';
import type { ZoneMapModelType } from 'common/models/flow/ZoneMap';
import uuidv4 from 'uuid/v4';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import L from 'leaflet';
import { type ThemeType } from 'react-css-themr';

import { MarkerType } from 'common/enums/MapEnum';
import TimeUtils from 'common/utils/TimeUtils';
import DefaultBulkViewEmptyState from 'common/entity/BulkViewEmptyState';

//Components
import TerritoryPlanningHeader from './TerritoryPlanningHeader';
import OverlayDialog from 'common/components/core/dialog/OverlayDialog';
import { EnhancedField } from 'common/components/core/form/Field';

import { connect } from 'react-redux';
import { withRouter } from 'common/components/core/router';
import {
    Box,
    Column,
    Columns,
    Stack,
    Text,
    Button as NeoButton
} from '@locus-taxy/neo-ui';
import CollapsibleDistributorList from './CollapsibleDistributorList';
import UnallocatedOutletList from './UnallocatedOutletList';
import theme from './territoryPlanning.scss';
import Loader from 'common/components/core/loader/Loader';
import TerritoryPolygon from './TerritoryPolygon';
import { Team } from 'common/models/internal/Team';
import type { MasterOutlet } from 'common/models/internal/MasterOutlet';
import type { TerritoryStatsType } from './TerritoryPlanningHeader';
import { Homebase } from 'common/models/internal/Homebase';

type TerritoryPropertyType = {
    distributor: string,
    assigned: boolean,
    color: string,
    MarkerIcon: HTMLElement,
    key: string
};

type TerritoryGeometryPropertiesType = Immutable.Map<
    string,
    TerritoryPropertyType
>;

const TerritoryGeometryProperties = Immutable.Map({
    blue: {
        distributor: null,
        assigned: false,
        color: neoUIColors.blue500.code,
        MarkerIcon: new L.icon({
            iconUrl: '/img/territory/territory-blue.svg',
            iconSize: [16, 16]
        }),
        key: 'blue'
    },
    green: {
        distributor: null,
        assigned: false,
        color: neoUIColors.green500.code,
        MarkerIcon: new L.icon({
            iconUrl: '/img/territory/territory-green.svg',
            iconSize: [16, 16]
        }),
        key: 'green'
    },
    yellow: {
        distributor: null,
        assigned: false,
        color: neoUIColors.yellow500.code,
        MarkerIcon: new L.icon({
            iconUrl: '/img/territory/territory-yellow.svg',
            iconSize: [16, 16]
        }),
        key: 'yellow'
    },
    purple: {
        distributor: null,
        assigned: false,
        color: neoUIColors.purple500.code,
        MarkerIcon: new L.icon({
            iconUrl: '/img/territory/territory-purple.svg',
            iconSize: [16, 16]
        }),
        key: 'purple'
    },
    lightBlue: {
        distributor: null,
        assigned: false,
        color: neoUIColors.blue200.code,
        MarkerIcon: new L.icon({
            iconUrl: '/img/territory/territory-light-blue.svg',
            iconSize: [16, 16]
        }),
        key: 'lightBlue'
    }
});

const UnallocatedMarker = new L.icon({
    iconUrl: '/img/territory/territory-red.svg',
    iconSize: [16, 16]
});

type HoveredElementType = {
    tooltip: React.Element<any>,
    latLng: TerritoryLatLng,
    meta: any
};

export type TerritoryPlanningContainerProps = {
    distributorFields: Array<string>,
    clientId: string,
    fetchOutlets: (clientId: string, filter: FilterBuilder) => void,
    fetchNonDistributorOutlets: (
        clientId: string,
        filter: FilterBuilder,
        page: number,
        size: number
    ) => void,
    fetchHomebases: (clientId: string, team: string) => void,
    defaultCountryCenter: {
        lat: number,
        lng: number
    },
    clientId: string,
    teams: Array<Team>,
    areDistributorOutletsLoading: boolean,
    areNonDistributorOutletsLoading: boolean,
    areHomebaseLoading: boolean,
    isOutletUpdateInProgress: boolean,
    unassignOutletInProgress: boolean,
    createMapInProgress: boolean,
    personnel: MasterPersonnel,
    updateOutlet: (
        params: Object,
        oldOUtlet: MasterOutlet,
        newOutlet: MasterOutlet
    ) => void,
    sortOutlet: (
        clientId: string,
        outletId: string,
        selectedHomebase: string
    ) => void,
    createMapApi: (clientId: string, zoneMap: ZoneMapModelType) => void,
    updateOutletStart: () => void,
    updateOutletSuccess: () => void,
    updateOutletError: (error: Error) => void,
    unassignOutletStart: () => void,
    unassignOutletSuccess: () => void,
    unassignOutletError: (error: Error) => void,
    history: RouterHistory,
    t: TFunction,
    theme: ThemeType
};

export type DistributorOutletObject = {
    selected: boolean,
    distributor: string,
    outlets: Array<MasterOutlet>
};

export type TerritoryLatLng = {
    lat: number,
    lng: number
};

export type TerritoryZoneType = {
    stats: TerritoryStatsType,
    distributor: string,
    latLngs: TerritoryLatLng,
    zone: string,
    center: TerritoryLatLng,
    polygon: Array<TerritoryLatLng>,
    color: string,
    markerIcon: ?HTMLElement
};

export type TerritoryPaginationType = {
    size: number,
    totalItems: number,
    totalPages: number,
    page: number,
    selectedPageIndex: number,
    pageSizeOptions: Array<number>
};
type TerritoryPlanningContainerState = {
    distributorSearchInput: ?string,
    distributorOutletMap: ?Immutable.Map<string, DistributorOutletObject>,
    filteredDistributors: ?Array<string>,
    allOutlets: ?Array<MasterOutlet>,
    nonDistributorOutlets: ?Array<MasterOutlet>,
    unallocatedCardExpaded: boolean,
    isDistributorExpanded: boolean,
    selectedOutlet: ?MasterOutlet,
    allDistributors: ?Array<string>,
    selectedZones: Array<TerritoryZoneType>,
    error: ?Error,
    mapCenter: ?TerritoryLatLng,
    zoom: number,
    unallocatedOutletSearchInput: ?string,
    selectedHomebase: ?{
        label: string,
        value: string
    },
    latestMapId: ?string,
    distributorSelectionError: ?string,
    homebaseError: ?string,
    saveMapDialogOpen: boolean,
    homebases: Array<Homebase>,
    geometryProperties: TerritoryGeometryPropertiesType,
    outletsPagination: ?TerritoryPaginationType,
    allOutletsCount: ?number,
    mapName: ?string,
    selectedTeam: ?Team,
    isSorting: boolean,
    mapHelper: ?OnMapLoadParams
};

export type OutletMarketLtLngTpye = {
    lat: number,
    lng: number,
    outletId: string,
    outletName: string,
    color: string,
    distributor: string,
    icon: HTMLElement,
    outlet: MasterOutlet
};

type OnMapLoadParams = { map: any, maps: any };

class TerritoryPlanningContainer extends React.Component<
    TerritoryPlanningContainerProps,
    TerritoryPlanningContainerState
> {
    constructor(props) {
        super(props);

        this.state = {
            distributorSearchInput: null,
            mapHelper: null,
            filteredDistributors: null,
            allOutlets: null,
            nonDistributorOutlets: null,
            unallocatedCardExpaded: false,
            selectedOutlet: null,
            isDistributorExpanded: false,
            allDistributors: null,
            selectedZones: [],
            distributorOutletMap: null,
            error: null,
            mapCenter: null,
            zoom: 7,
            unallocatedOutletSearchInput: null,
            selectedHomebase: null,
            latestMapId: null,
            distributorSelectionError: null,
            homebaseError: null,
            saveMapDialogOpen: false,
            mapName: null,
            homebases: [],
            geometryProperties: TerritoryGeometryProperties,
            outletsPagination: null,
            allOutletsCount: null,
            selectedTeam: null,
            isSorting: false
        };
    }

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(prevProps) {
        const {
            clientId: prevClientId,
            distributorFields: prevDistributorFields
        } = prevProps;
        const { clientId, distributorFields } = this.props;
        if (
            clientId !== prevClientId ||
            prevDistributorFields.length !== distributorFields.length
        ) {
            this.loadData();
        }
    }

    loadData = () => {
        this.fetchDistributorOutlets();
        this.fetchNonDistributorOutlets();
    };

    fetchDistributorOutlets = () => {
        const {
            distributorFields,
            fetchOutlets,
            clientId,
            defaultCountryCenter
        } = this.props;
        const { distributorSearchInput, geometryProperties } = this.state;
        if (distributorFields && distributorFields.length) {
            const distributorFilter = this.getDistributorFilter(
                distributorFields
            );

            fetchOutlets(clientId, distributorFilter)
                .then(response => {
                    const filteredFields = distributorSearchInput
                        ? distributorFields.filter(distributor => {
                              return distributor.includes(
                                  distributorSearchInput
                              );
                          })
                        : distributorFields;
                    const distributorOutletMap = this.createOutletDistributorMap(
                        response.outlets,
                        filteredFields
                    );

                    const zones = this.getUpdatedZones(
                        distributorOutletMap,
                        geometryProperties
                    );

                    this.setState({
                        allOutlets: response.outlets,
                        allDistributors: distributorFields,
                        distributorOutletMap,
                        mapCenter: defaultCountryCenter,
                        filteredDistributors: filteredFields,
                        selectedZones: zones
                    });
                })
                .catch(error => {
                    this.setState({
                        error
                    });
                });
        }
    };

    fetchNonDistributorOutlets = () => {
        const {
            clientId,
            teams,
            fetchNonDistributorOutlets,
            distributorFields
        } = this.props;
        const { outletsPagination } = this.state;
        if (distributorFields && distributorFields.length) {
            const nonDistributorFilter = this.getNonDistributorFilter(
                distributorFields
            );
            const selectedPage = outletsPagination ? outletsPagination.page : 1;
            const pageSize = outletsPagination ? outletsPagination.size : 10;
            this.fetchHomebases(teams.first().teamId);
            fetchNonDistributorOutlets(
                clientId,
                nonDistributorFilter,
                selectedPage,
                pageSize
            )
                .then(response => {
                    const filteredEmptyOutlet = response.outlets?.filter(
                        store => {
                            return !store.customProperties.distributor;
                        }
                    );
                    const { paginationInfo } = response;
                    const updatedOutletsPagination = this.getDefaultPaginationDetails(
                        paginationInfo,
                        pageSize
                    );
                    this.setState({
                        allOutletsCount: paginationInfo.total,
                        nonDistributorOutlets: filteredEmptyOutlet || [],
                        outletsPagination: updatedOutletsPagination,
                        selectedTeam: teams.first().teamId
                    });
                })
                .catch(error => {
                    this.setState({
                        error
                    });
                });
        }
    };

    fetchHomebases = team => {
        const { clientId, fetchHomebases } = this.props;
        fetchHomebases(clientId, team).then(response => {
            this.setState({
                homebases: response
            });
        });
    };

    getDefaultPaginationDetails: (
        paginationInfo: TerritoryPaginationType,
        pageSize: ?number
    ) => TerritoryPaginationType = (paginationInfo, pageSize) => {
        const totalPages = paginationInfo.numberOfPages;
        return {
            size: pageSize,
            totalItems: paginationInfo.total,
            totalPages,
            page: paginationInfo.currentPage,
            selectedPageIndex: paginationInfo.currentPage - 1,
            pageSizeOptions: [5, 10, 15]
        };
    };

    getDistributorFilter = distributorFields => {
        const statusFilter = new FilterBuilder()
            .withName('status')
            .withOperation(FilterOperation.Equals)
            .withValues('ACTIVE');
        const distributorFilter = new FilterBuilder()
            .withName('customProperties.distributor')
            .withOperation(FilterOperation.Equals)
            .withValues(distributorFields);
        return new FilterListBuilder()
            .addAll([distributorFilter, statusFilter])
            .build();
    };
    getNonDistributorFilter = distributorFields => {
        const { unallocatedOutletSearchInput } = this.state;
        const statusFilter = new FilterBuilder()
            .withName('status')
            .withOperation(FilterOperation.Equals)
            .withValues('ACTIVE');
        const nonDistributorFilter = new FilterBuilder()
            .withName('customProperties.distributor')
            .withOperation(FilterOperation.NotEquals)
            .withLogicalOperation(LogicalOperation.AND)
            .withValues(distributorFields)
            .withAllowEmptyOrNull(true)
            .withCaseSensitive(false);
        if (unallocatedOutletSearchInput) {
            const searchFilter = new FilterBuilder()
                .withName('name')
                .withOperation(FilterOperation.ContainsToken)
                .withValue(unallocatedOutletSearchInput);
            return new FilterListBuilder()
                .addAll([nonDistributorFilter, statusFilter, searchFilter])
                .build();
        }
        return new FilterListBuilder()
            .addAll([nonDistributorFilter, statusFilter])
            .build();
    };

    createOutletDistributorMap = (allOutlets, allDistributors) => {
        let distributorMap = Immutable.Map();
        const { selectedZones } = this.state;
        allOutlets?.forEach(outlet => {
            const outletDistributor = outlet.customProperties.distributor;
            if (distributorMap.has(outletDistributor)) {
                const exitingOutletObject = distributorMap.get(
                    outletDistributor
                );
                const outletList = exitingOutletObject.outlets;
                const updatedOutletList = [...outletList, outlet];
                const outletObject = {
                    selected: exitingOutletObject.selected,
                    outlets: updatedOutletList,
                    distributor: outletDistributor
                };
                distributorMap = distributorMap.set(
                    outletDistributor,
                    outletObject
                );
            } else {
                const isSelected = selectedZones.find(
                    zone => zone.distributor === outletDistributor
                );
                const outletObject = {
                    selected: isSelected,
                    outlets: [outlet],
                    distributor: outletDistributor
                };
                distributorMap = distributorMap.set(
                    outletDistributor,
                    outletObject
                );
            }
        });
        allDistributors?.forEach(distributor => {
            if (!distributorMap.has(distributor)) {
                distributorMap = distributorMap.set(distributor, {
                    outlets: [],
                    distributor,
                    selected: false
                });
            }
        });
        return distributorMap;
    };

    onDistributorToggle = () => {
        const { isDistributorExpanded } = this.state;
        this.setState({
            isDistributorExpanded: !isDistributorExpanded
        });
    };

    onUnallocatedCardToggle = () => {
        const { unallocatedCardExpaded } = this.state;
        this.setState({
            unallocatedCardExpaded: !unallocatedCardExpaded
        });
    };

    updateOutletsPagination = pagination => {
        this.setState(
            {
                outletsPagination: pagination
            },
            () => {
                this.fetchNonDistributorOutlets();
            }
        );
    };

    onOutletsPageSizeChange = pagination => {
        const updatedOutletPagination = {
            size: pagination.size,
            totalItems: pagination.totalItems,
            totalPages: pagination.totalPages,
            page: pagination.page,
            selectedPageIndex: pagination.selectedPageIndex,
            pageSizeOptions: pagination.pageSizeOptions
        };
        this.setState(
            {
                outletsPagination: updatedOutletPagination
            },
            () => {
                this.fetchNonDistributorOutlets();
            }
        );
    };

    getBounds = () => {
        const { distributorOutletMap, selectedOutlet } = this.state;
        let totalOutletList = [];
        distributorOutletMap?.mapEntries(([key]) => {
            const outletObject = distributorOutletMap.get(key);
            if (outletObject.selected) {
                const outletLatLngList = this.getLatLngListFromOutlets(
                    outletObject.outlets
                );
                if (outletLatLngList) {
                    totalOutletList = totalOutletList.concat(outletLatLngList);
                }
            }
        });
        let updatedBounds;
        if (selectedOutlet) {
            const selectedOutletLatLng =
                selectedOutlet.locationInfo.location?.latLng;
            totalOutletList.push({
                lat: selectedOutletLatLng?.lat,
                lng: selectedOutletLatLng?.lng
            });
        }
        if (!totalOutletList.length) {
            updatedBounds = null;
        } else {
            updatedBounds = this.calculatMapBounds(totalOutletList);
        }
        return updatedBounds;
    };

    getLatLngListFromOutlets = outlets => {
        const latLngList = outlets?.map(outlet => {
            const { latLng } = outlet.locationInfo.location;
            return latLng;
        });
        return latLngList;
    };

    getCenterForPolygon(latLngList) {
        const bounds = new google.maps.LatLngBounds();
        latLngList?.forEach(outlet => {
            if (outlet) {
                const { lat, lng } = outlet;
                bounds.extend(new google.maps.LatLng(lat, lng));
            }
        });
        const zoneBounds = bounds.getNorthEast();
        return {
            lat: zoneBounds.lat(),
            lng: zoneBounds.lng()
        };
    }

    calculatMapBounds = latLngList => {
        const bounds = new google.maps.LatLngBounds();
        if (latLngList.length === 1) {
            const pointLatLng = latLngList[0];
            const extendPoint1 = new google.maps.LatLng(
                pointLatLng.lat + 0.01,
                pointLatLng.lng + 0.01
            );
            const extendPoint2 = new google.maps.LatLng(
                pointLatLng.lat - 0.01,
                pointLatLng.lng - 0.01
            );
            bounds.extend(extendPoint1);
            bounds.extend(extendPoint2);
            return [
                [extendPoint1.lat(), extendPoint1.lng()],
                [extendPoint2.lat(), extendPoint2.lng()]
            ];
        }

        let x1;
        let x2;
        let y1;
        let y2;
        latLngList.forEach(latLng => {
            if (latLng) {
                if (!x1 && !y1) {
                    x1 = x2 = latLng?.lat;
                    y1 = y2 = latLng?.lng;
                } else if (latLng.lat && latLng.lng) {
                    x2 = latLng.lat > x2 ? latLng.lat : x2;
                    x1 = latLng.lat < x1 ? latLng.lat : x1;
                    y1 = latLng.lng < y1 ? latLng.lng : y1;
                    y2 = latLng.lng > y2 ? latLng.lng : y2;
                }
            }
        });

        const updatedBounds = [
            [x2, y2],
            [x1, y1]
        ];
        return updatedBounds;
    };

    onOutletPageChange = pagination => {
        const updatedOutletPagination = {
            size: pagination.size,
            totalItems: pagination.totalItems,
            totalPages: pagination.totalPages,
            page: pagination.page,
            selectedPageIndex: pagination.selectedPageIndex,
            pageSizeOptions: pagination.pageSizeOptions
        };
        this.setState(
            {
                outletsPagination: updatedOutletPagination
            },
            () => {
                this.fetchNonDistributorOutlets();
            }
        );
    };

    getUpdatedZones = (distributorOutletMap, geometryProperties) => {
        const distributorZones = [];
        distributorOutletMap.mapEntries(([key]) => {
            const outletObject = distributorOutletMap.get(key);
            const { outlets } = outletObject;
            let totalRevenue = 0;
            let totalTransactionTime = 0;
            outlets.forEach(outlet => {
                const { recentRevenue } = outlet.historicalData;
                if (recentRevenue) {
                    const {
                        amount: revenue,
                        transactionDuration
                    } = recentRevenue.amount;
                    totalRevenue = revenue
                        ? totalRevenue + revenue
                        : totalRevenue;
                    totalTransactionTime = transactionDuration
                        ? totalTransactionTime + transactionDuration
                        : totalTransactionTime;
                }
            });
            const format = { h: () => 'HR', m: () => 'MIN', s: () => 'SEC' };
            const tat = TimeUtils.humanizeSeconds(totalTransactionTime, {
                format,
                largest: 2
            });
            const stats = {
                totalOutlets: outlets.length,
                totalRevenue,
                avgRevenue: totalRevenue / outlets.length,
                tat
            };
            const isDistributorSelected = outletObject.selected;
            if (isDistributorSelected) {
                const outletLatLngList = this.getLatLngListFromOutlets(
                    outletObject.outlets
                );
                const latLngListArray = this.getLatLngListArray(
                    outletLatLngList
                );
                const distributorPolygon = InnerPolygonMarker.polySort(
                    latLngListArray
                );
                const distributorTerritoryProperty = geometryProperties
                    ?.filter(
                        property =>
                            property.distributor === outletObject.distributor
                    )
                    .first();
                const center = this.getCenterForPolygon(outletLatLngList);
                distributorZones.push({
                    stats,
                    distributor: outletObject.distributor,
                    latLngs: outletLatLngList,
                    zone: key,
                    center: { lat: center.lat, lng: center.lng },
                    polygon: distributorPolygon,
                    color: distributorTerritoryProperty?.color,
                    markerIcon: distributorTerritoryProperty?.MarkerIcon
                });
            }
        });

        const polygonZones = [...distributorZones];
        return polygonZones;
    };

    getLatLngListArray = list => {
        return list?.map(latLng => {
            return [latLng?.lat, latLng?.lng];
        });
    };

    selectDistributorToggle = (distributor, isChecked) => {
        if (isChecked) {
            this.selectDistributor(distributor);
        } else {
            this.deselectDistributor(distributor);
        }
    };

    assignColorToDistributor = (distributor, geometryProperties) => {
        const unassignedColorProperty = geometryProperties.filter(
            colorProperty => colorProperty.distributor === null
        );
        let updatedGeometryProperties = geometryProperties;
        if (unassignedColorProperty.size) {
            const property = unassignedColorProperty.first();
            const updatedProperty = Object.assign({}, property, {
                distributor
            });
            updatedGeometryProperties = geometryProperties.set(
                property.key,
                updatedProperty
            );
            return updatedGeometryProperties;
        }
    };

    unassignColorFromDistributor = (distributor, geometryProperties) => {
        const assignedColorProperty = geometryProperties.filter(
            colorProperty => colorProperty.distributor === distributor
        );
        let updatedGeometryProperties;
        if (assignedColorProperty.size) {
            const property = assignedColorProperty.first();
            const updatedProperty = Object.assign({}, property, {
                distributor: null
            });
            updatedGeometryProperties = geometryProperties.set(
                property.key,
                updatedProperty
            );
        }
        return updatedGeometryProperties;
    };

    selectDistributor = distributor => {
        const { t } = this.props;
        const { distributorOutletMap, geometryProperties } = this.state;
        const distributorObject = distributorOutletMap.get(distributor);
        const updatedDistributorObject = {
            selected: true,
            outlets: distributorObject.outlets,
            distributor
        };
        const updatedDistributorOutletMap = distributorOutletMap.set(
            distributor,
            updatedDistributorObject
        );
        const updatedGeometryProperties = this.assignColorToDistributor(
            distributor,
            geometryProperties
        );
        const updatedZones = this.getUpdatedZones(
            updatedDistributorOutletMap,
            updatedGeometryProperties
        );

        this.setState({
            distributorOutletMap: updatedDistributorOutletMap,
            selectedZones: updatedZones,
            geometryProperties: updatedGeometryProperties,
            distributorSelectionError:
                updatedZones.length > 4
                    ? t('territory.distributionSelectionError')
                    : null
        });
    };

    deselectDistributor = distributor => {
        const { t } = this.props;
        const { distributorOutletMap, geometryProperties } = this.state;
        const distributorObject = distributorOutletMap?.get(distributor);
        const updatedDistributorObject = {
            selected: false,
            outlets: distributorObject?.outlets,
            distributor,
            color: distributorObject?.color
        };
        const updatedDistributorOutletMap = distributorOutletMap?.set(
            distributor,
            updatedDistributorObject
        );
        const updatedGeometryProperties = this.unassignColorFromDistributor(
            distributor,
            geometryProperties
        );
        const updatedZones = this.getUpdatedZones(
            updatedDistributorOutletMap,
            updatedGeometryProperties
        );
        /*FIXME: distributorSelectionError
            just boolean field can be enough didn't find any place where we are using this msg
            need to check with @abhishek-orion
        */
        this.setState({
            distributorOutletMap: updatedDistributorOutletMap,
            selectedZones: updatedZones,
            geometryProperties: updatedGeometryProperties,
            distributorSelectionError:
                updatedZones.length >= 5
                    ? t('territory.distributionSelectionError')
                    : null
        });
    };

    onOutletSelect = outletId => {
        const { nonDistributorOutlets, selectedOutlet } = this.state;
        const newSelectedOutlet = nonDistributorOutlets?.find(
            outlet => outletId === outlet.id
        );

        if (selectedOutlet && selectedOutlet.id === newSelectedOutlet?.id) {
            this.setState({
                selectedOutlet: null
            });
        } else {
            this.setState({
                selectedOutlet: newSelectedOutlet
            });
        }
    };

    getMapCenter = () => {
        const {
            selectedZones,
            allDistributors,
            distributorOutletMap,
            nonDistributorOutlets,
            selectedOutlet
        } = this.state;
        if (selectedZones === null) {
            let totalOutletList = [];
            allDistributors?.forEach(distributor => {
                const distributorObject = distributorOutletMap?.get(
                    distributor
                );
                const distributorOutletList = this.getLatLngListFromOutlets(
                    distributorObject?.outlets
                );
                totalOutletList = [...totalOutletList, distributorOutletList];
            });
            const nonDistributorOutletList = this.getLatLngListFromOutlets(
                nonDistributorOutlets
            );
            totalOutletList = [...totalOutletList, nonDistributorOutletList];
            const centerList = totalOutletList.map(latLng => {
                return { lat: latLng.lat, lng: latLng.lng };
            });
            const mapCenter = this.getCenterForPolygon(centerList);
            this.setState({
                mapCenter
            });
        }
        if (selectedOutlet) {
            const selectedOutletLatLng =
                selectedOutlet.locationInfo.location?.latLng;
            return {
                lat: selectedOutletLatLng?.lat,
                lng: selectedOutletLatLng?.lng
            };
        }
        const centerList = selectedZones.map(zone => {
            return { lat: zone.center.lat, lng: zone.center.lng };
        });
        const mapCenter = this.getCenterForPolygon(centerList);
        const mapCenterLatLng = { lat: mapCenter.lat, lng: mapCenter.lng };
        this.setState({
            mapCenter: mapCenterLatLng
        });
    };

    updateOutlet = (params, oldOUtlet, newOutlet, isUnassign) => {
        const {
            updateOutlet,
            updateOutletStart,
            updateOutletSuccess,
            updateOutletError,
            unassignOutletStart,
            unassignOutletSuccess,
            unassignOutletError
        } = this.props;
        if (isUnassign) {
            unassignOutletStart();
        } else {
            updateOutletStart();
        }
        updateOutlet(params, oldOUtlet, newOutlet)
            .then(() => {
                this.loadData();
                if (isUnassign) {
                    unassignOutletSuccess();
                } else {
                    updateOutletSuccess();
                }
                this.setState({
                    selectedOutlet: null
                });
            })
            .catch(error => {
                if (isUnassign) {
                    unassignOutletError(error);
                } else {
                    updateOutletError(error);
                }
            });
    };

    renderOutletMarkers = () => {
        const {
            distributorOutletMap,
            selectedOutlet,
            allDistributors,
            selectedHomebase,
            selectedTeam,
            selectedZones
        } = this.state;
        const {
            clientId,
            sortOutlet,
            isOutletUpdateInProgress,
            unassignOutletInProgress,
            isSorting,
            t
        } = this.props;
        let totalOutletList = [];
        distributorOutletMap?.mapEntries(([key]) => {
            const outletObject = distributorOutletMap.get(key);
            if (outletObject.selected) {
                const outletList = outletObject.outlets.map(outlet => {
                    if (outlet) {
                        const { latLng } = outlet.locationInfo.location;
                        const outletDistributor =
                            outlet.customProperties.distributor;
                        const selectedTerritory = selectedZones.find(zone => {
                            return zone.distributor === outletDistributor;
                        });
                        const icon = selectedTerritory?.markerIcon;
                        const { color } = selectedTerritory;

                        return {
                            lat: latLng?.lat,
                            lng: latLng?.lng,
                            outletId: outlet.id,
                            outletName: outlet.name,
                            color,
                            distributor: outletDistributor,
                            icon,
                            outlet
                        };
                    }
                });
                if (outletList) {
                    totalOutletList = totalOutletList.concat(outletList);
                }
            }
        });
        const oulletMarkers = totalOutletList.map(latLngObject => {
            return (
                <OutletMarker
                    t={t}
                    key={latLngObject.outlet.id}
                    validateTeamAndHomebase={this.validateTeamAndHomebase}
                    unassignOutletInProgress={unassignOutletInProgress}
                    isOutletUpdateInProgress={isOutletUpdateInProgress}
                    isSorting={isSorting}
                    outletAssigned
                    selectedTeam={selectedTeam}
                    selectedHomebase={selectedHomebase}
                    sortOutlet={sortOutlet}
                    clientId={clientId}
                    updateOutlet={this.updateOutlet}
                    allDistributors={allDistributors}
                    theme={theme}
                    latLngObject={latLngObject}
                    position={latLngObject.latLng}
                />
            );
        });
        if (selectedOutlet) {
            const selectedOutletLatLng =
                selectedOutlet.locationInfo.location?.latLng;

            const unallocatedOutletObject = {
                lat: selectedOutletLatLng?.lat,
                lng: selectedOutletLatLng?.lng,
                outletId: selectedOutlet.id,
                outletName: selectedOutlet.name,
                color: neoUIColors.red500.code,
                distributor: null,
                outlet: selectedOutlet,
                icon: UnallocatedMarker
            };
            const unallocatedOutletMarker = (
                <OutletMarker
                    t={t}
                    validateTeamAndHomebase={this.validateTeamAndHomebase}
                    isOutletUpdateInProgress={isOutletUpdateInProgress}
                    isSorting={isSorting}
                    selectedHomebase={selectedHomebase}
                    selectedTeam={selectedTeam}
                    sortOutlet={sortOutlet}
                    clientId={clientId}
                    updateOutlet={this.updateOutlet}
                    allDistributors={allDistributors}
                    latLngObject={unallocatedOutletObject}
                    theme={theme}
                    position={selectedOutletLatLng}
                />
            );
            oulletMarkers.push(unallocatedOutletMarker);
        }
        return oulletMarkers;
    };

    noOp = () => {};

    renderTooltip(hoveredElement: HoveredElementType) {
        var { tooltip, latLng } = hoveredElement;
        return (
            <span
                key={1}
                lat={latLng?.lat()}
                lng={latLng?.lng()}
                markerType={MarkerType.NON_HOVERABLE}
                onMouseOver={this.noOp}
                onMouseOut={this.noOp}
                className={theme.mapTooltip}
            >
                {tooltip}
            </span>
        );
    }

    renderPolygons = () => {
        const { selectedZones } = this.state;
        const { t } = this.props;
        const mapPolygons = selectedZones.map((zone, index) => {
            return (
                <TerritoryPolygon
                    key={zone.zone}
                    t={t}
                    index={index}
                    theme={theme}
                    zone={zone}
                    id={zone.zone}
                />
            );
        });
        return mapPolygons;
    };

    onDistributorSearchInputChange = value => {
        const { allDistributors, allOutlets } = this.state;
        let filteredFields;
        if (value) {
            filteredFields = allDistributors?.filter(distributor =>
                distributor.includes(value)
            );
        } else if (allDistributors) {
            filteredFields = [...allDistributors];
        }
        const distributorOutletMap = this.createOutletDistributorMap(
            allOutlets,
            filteredFields
        );
        this.setState({
            distributorSearchInput: value,
            distributorOutletMap,
            filteredDistributors: filteredFields
        });
    };

    onOutletSearchChange = value => {
        const { outletsPagination } = this.state;
        const updatedPagination = { ...outletsPagination, page: 1 };
        this.setState(
            {
                unallocatedOutletSearchInput: value,
                outletsPagination: updatedPagination
            },
            () => {
                this.fetchNonDistributorOutlets();
            }
        );
    };

    getHomebaseList = () => {
        const { homebases } = this.state;
        if (homebases) {
            const homebaseOptions = homebases.map(homebase => {
                return {
                    label: homebase.name,
                    value: homebase.teams[0].teamId
                };
            });
            return homebaseOptions;
        }
        return [];
    };

    onHomebaseChange = homebase => {
        this.setState({
            selectedHomebase: homebase,
            homebaseError: null
        });
    };

    validateTeamAndHomebase = () => {
        const { selectedHomebase } = this.state;
        const { t } = this.props;
        if (!selectedHomebase) {
            this.setState({
                homebaseError: t('territory.homebaseError')
            });
            return t('territory.homebaseError');
        }
        this.setState({
            homebaseError: null
        });
        return false;
    };

    onSaveMapClick = () => {
        const isInvalid = this.validateTeamAndHomebase();
        if (isInvalid) {
            return;
        }
        this.saveMapDialogOpen();
    };

    createZoneMapforDistributors = () => {
        const { clientId, createMapApi, personnel } = this.props;
        const {
            selectedZones,
            selectedTeam,
            selectedHomebase,
            mapName
        } = this.state;
        const territoryGeometries = selectedZones.map(zone => {
            let geometryId = uuidv4();
            geometryId = geometryId.replace(/-/g, '');
            const mapId = uuidv4().replace(/-/g, '');
            const updatedVerticesLatLng = zone.polygon.map(latLng => {
                return {
                    lat: latLng[0],
                    lng: latLng[1]
                };
            });
            return {
                actor: { id: `mara/personnel/${personnel}` },
                geometry: {
                    polygon: {
                        type: 'POLYGON',
                        vertices: updatedVerticesLatLng
                    }
                },
                id: {
                    geometryId,
                    mapId
                },
                name: zone.zone,
                routeId: zone.zone.replace(/ /g, '-'),
                visualProperties: {
                    color: zone.color,
                    opacity: 1
                }
            };
        });
        const zoneMapObject = {
            clientId,
            actor: {},
            customProperties: {},
            folders: [],
            geometries: territoryGeometries,
            status: 'ACTIVE',
            name: mapName,
            homebaseId: {
                clientId,
                locationId: selectedHomebase?.value
            },
            summary: { zonesCount: territoryGeometries.length + 1 },
            teamId: {
                clientId,
                teamId: selectedTeam
            }
        };
        const newZoneMap = zoneMapObject;

        createMapApi(clientId, newZoneMap).then(response => {
            const createdMapId = response.id;
            this.setState({
                latestMapId: createdMapId,
                saveMapDialogOpen: false,
                mapName: null
            });
        });
    };

    onMapNameChange = name => {
        this.setState({
            mapName: name
        });
    };

    saveMapDialogOpen = () => {
        this.setState({
            saveMapDialogOpen: true
        });
    };

    saveMapDialogClose = () => {
        this.setState({
            saveMapDialogOpen: false
        });
    };

    viewLatestMap = () => {
        const { history, clientId } = this.props;
        const { latestMapId } = this.state;
        history.push({
            pathname: `/client/${clientId}/entity/map/${latestMapId}`
        });
    };

    getTerritoryStats = () => {
        const { distributorOutletMap, allDistributors } = this.state;
        let totalOutlets = 0;
        let totalRevenue = 0;
        if (distributorOutletMap) {
            distributorOutletMap.mapEntries(([key]) => {
                const outletObject = distributorOutletMap.get(key);
                const { outlets } = outletObject;
                totalOutlets = totalOutlets + outlets.length;
                let distributorRevenue = 0;
                outlets.forEach(outlet => {
                    const revenue =
                        outlet.historicalData?.recentRevenue?.amount;
                    distributorRevenue =
                        distributorRevenue + revenue ? revenue : 0;
                });
                totalRevenue = totalRevenue + distributorRevenue;
            });
            let TerritoryStats;
            if (allDistributors?.length) {
                TerritoryStats = {
                    totalOutlets,
                    totalDistributors: allDistributors.length,
                    avgStoresPerTerritory:
                        totalOutlets / allDistributors.length,
                    avgRevenuePerTerritory:
                        totalRevenue / allDistributors.length
                };
            }
            return TerritoryStats;
        }
        return null;
    };

    onMapLoad = ({ map, maps }: OnMapLoadParams) => {
        this.setState({ mapHelper: { map, maps } });
        map.setOptions({ scaleControl: true });
    };

    render() {
        const {
            allDistributors,
            distributorOutletMap,
            isDistributorExpanded,
            nonDistributorOutlets,
            selectedOutlet,
            unallocatedCardExpaded,
            mapCenter,
            zoom,
            unallocatedOutletSearchInput,
            distributorSearchInput,
            filteredDistributors,
            selectedHomebase,
            allOutlets,
            homebaseError,
            saveMapDialogOpen,
            mapName,
            selectedTeam,
            selectedZones,
            distributorSelectionError,
            latestMapId,
            outletsPagination,
            allOutletsCount
        } = this.state;
        const {
            areDistributorOutletsLoading,
            areNonDistributorOutletsLoading,
            areHomebaseLoading,
            createMapInProgress,
            distributorFields,
            t
        } = this.props;
        if (!distributorFields || !distributorFields.length) {
            return (
                <DefaultBulkViewEmptyState
                    imageSrc="/img/zone-maps-master-empty.png"
                    title={t('territory.noDistributorData')}
                    entityNameId="distributor"
                    disableAddButton
                />
            );
        }
        const isLoading = !(allOutlets && nonDistributorOutlets);
        return (
            <Box className={theme.territoryPlanningContainer}>
                <Stack height="100vh">
                    <TerritoryPlanningHeader
                        t={t}
                        territoryStats={this.getTerritoryStats()}
                        allOutletsCount={allOutletsCount}
                        latestMapId={latestMapId}
                        selectedZones={selectedZones}
                        selectedTeam={selectedTeam}
                        homebaseError={homebaseError}
                        isHomebaseLoading={areHomebaseLoading}
                        onHomebaseChange={this.onHomebaseChange}
                        getHomebaseList={this.getHomebaseList}
                        theme={theme}
                        onSaveMapClick={this.onSaveMapClick}
                        viewLatestMap={this.viewLatestMap}
                        selectedHomebase={selectedHomebase}
                    />
                    <Columns>
                        <Column width="2/5">
                            <Stack height="92vh">
                                <Box
                                    height="40vh"
                                    className={theme.collapsibleList}
                                >
                                    <CollapsibleDistributorList
                                        t={t}
                                        selectedZones={selectedZones}
                                        distributorSelectionError={
                                            distributorSelectionError
                                        }
                                        areDistributorOutletsLoading={
                                            areDistributorOutletsLoading
                                        }
                                        distributorSearchInput={
                                            distributorSearchInput
                                        }
                                        onSearchInputChange={
                                            this.onDistributorSearchInputChange
                                        }
                                        theme={theme}
                                        selectDistributorToggle={
                                            this.selectDistributorToggle
                                        }
                                        onDistributorToggle={
                                            this.onDistributorToggle
                                        }
                                        distributorOutletMap={
                                            distributorOutletMap
                                        }
                                        allDistributors={filteredDistributors}
                                        isExpandible
                                        isExpanded={isDistributorExpanded}
                                    />
                                </Box>
                                <Box
                                    height="52vh"
                                    className={theme.collapsibleList}
                                >
                                    <UnallocatedOutletList
                                        t={t}
                                        onPageSizeChange={
                                            this.onOutletsPageSizeChange
                                        }
                                        outletsPagination={outletsPagination}
                                        updateOutletsPagination={
                                            this.updateOutletsPagination
                                        }
                                        unallocatedOutletSearchInput={
                                            unallocatedOutletSearchInput
                                        }
                                        onSearchInputChange={
                                            this.onOutletSearchChange
                                        }
                                        areNonDistributorOutletsLoading={
                                            areNonDistributorOutletsLoading
                                        }
                                        onPageChange={this.onOutletPageChange}
                                        onOutletSelect={this.onOutletSelect}
                                        selectedOutlet={selectedOutlet}
                                        theme={theme}
                                        onUnallocatedCardToggle={
                                            this.onUnallocatedCardToggle
                                        }
                                        nonDistributorOutlets={
                                            nonDistributorOutlets
                                        }
                                        allDistributors={allDistributors}
                                        isExpandible
                                        isExpanded={unallocatedCardExpaded}
                                    />
                                </Box>
                            </Stack>
                        </Column>
                        <Column witdh="fluid">
                            <Box className={theme.leafletMapContainer}>
                                {isLoading ? (
                                    <Box>
                                        <Loader active />
                                    </Box>
                                ) : (
                                    <LeafletMap
                                        onGoogleApiLoaded={this.onMapLoad}
                                        bounds={this.getBounds()}
                                        center={[mapCenter.lat, mapCenter.lng]}
                                        zoomControl={false}
                                        zoom={zoom}
                                        zoomPosition="topleft"
                                        closePopupOnClick={false}
                                    >
                                        <ZoomControl position="topleft" />
                                        <LeafletConsumer>
                                            {context => (
                                                <GoogleLayer
                                                    type={MapTypeEnum.Roadmap}
                                                    leaflet={context}
                                                />
                                            )}
                                        </LeafletConsumer>
                                        {this.renderPolygons()}
                                        {this.renderOutletMarkers()}
                                    </LeafletMap>
                                )}
                            </Box>
                        </Column>
                    </Columns>
                </Stack>
                <OverlayDialog
                    active={saveMapDialogOpen}
                    theme={theme}
                    onEscapeClick={this.saveMapDialogClose}
                    dialogClassName={theme.saveMapDialog}
                >
                    <Box height="200px">
                        <Stack space="medium">
                            <Box
                                p={2}
                                backgroundColor={neoUIColors.blue500.name}
                                sx={{ color: 'white' }}
                            >
                                <Text fontWeight="600" fontSize={3}>
                                    {t('territory.saveDistMap')}
                                </Text>
                            </Box>
                            <Box p={2}>
                                <EnhancedField
                                    type="text"
                                    element="input"
                                    value={mapName}
                                    label={t('territory.mapName')}
                                    theme={theme}
                                    onChange={this.onMapNameChange}
                                />
                            </Box>
                            <Box p={2}>
                                <Columns
                                    alignY="center"
                                    align="center"
                                    space="medium"
                                >
                                    <NeoButton
                                        isLoading={createMapInProgress}
                                        disabled={!mapName}
                                        palette="primary"
                                        appearance="solid"
                                        onClick={
                                            this.createZoneMapforDistributors
                                        }
                                    >
                                        {t('territory.saveMap')}
                                    </NeoButton>
                                    <NeoButton
                                        palette="primary"
                                        appearance="outline"
                                        onClick={this.saveMapDialogClose}
                                    >
                                        {t('territory.cancel')}
                                    </NeoButton>
                                </Columns>
                            </Box>
                        </Stack>
                    </Box>
                </OverlayDialog>
            </Box>
        );
    }
}

const mapStateToProps = (state, props) => {
    const distributorFields = getCustomDistributorField(state, props)
        ? getCustomDistributorField(state, props).toJS()
        : null;
    const clientId = clientIdSelector(state, props);
    const teams = teamsSelector(state, props);
    let activeTeams;
    if (teams) {
        activeTeams = teams.filter(team => {
            return team.status === 'ACTIVE';
        });
    }
    const defaultCountryCenter = ClientSelectors.getCountryCenterFromLocaleSettings(
        state,
        props
    );
    const areDistributorOutletsLoading = isDistributorOutletsLoading(state);
    const areNonDistributorOutletsLoading = isNonDistributorOutletsLoading(
        state
    );
    const areHomebaseLoading = isHomebaseLoading(state);
    const isOutletUpdateInProgress = isUpdateOutletInProgress(state);
    const unassignOutletInProgress = isUnassignOutletInProgress(state);
    const createMapInProgress = isCreateMapInProgress(state);
    const personnel = personnelSelector(state);
    const isSorting = isSortInProgress(state);
    return {
        distributorFields,
        clientId,
        teams: activeTeams,
        defaultCountryCenter,
        areDistributorOutletsLoading,
        areNonDistributorOutletsLoading,
        areHomebaseLoading,
        isOutletUpdateInProgress,
        unassignOutletInProgress,
        createMapInProgress,
        personnel,
        isSorting
    };
};

export default (compose(
    withRouter,
    connect(mapStateToProps, {
        fetchOutlets: TerritoryPlanningActions.fetchDistributorOutlets,
        fetchNonDistributorOutlets:
            TerritoryPlanningActions.fetchNonDistributorOutlets,
        updateOutlet: OutletActions.patchEntity,
        createMapApi: TerritoryPlanningActions.createZoneMap,
        fetchHomebases: TerritoryPlanningActions.fetchTeamHomebases,
        sortOutlet: TerritoryPlanningActions.sortOutlet,
        updateOutletStart: TerritoryPlanningActions.updateOutletStart,
        updateOutletSuccess: TerritoryPlanningActions.updateOutletSuccess,
        updateOutletError: TerritoryPlanningActions.updateOutletError,
        unassignOutletStart: TerritoryPlanningActions.unassignOutletStart,
        unassignOutletSuccess: TerritoryPlanningActions.unassignOutletSuccess,
        unassignOutletError: TerritoryPlanningActions.unassignOutletError
    }),
    translate('entityManagement')
)(TerritoryPlanningContainer): any);
